import type { ComponentType, ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../lib/auth.js";
import { IconBoard, IconEvents, IconMatches, IconMe, IconPlay } from "./icons.js";

interface Tab {
  to: string;
  label: string;
  Icon: ComponentType<{ size?: number }>;
}

const TABS: Tab[] = [
  { to: "/board", label: "Board", Icon: IconBoard },
  { to: "/events", label: "Events", Icon: IconEvents },
  { to: "/log", label: "Log", Icon: IconPlay },
  { to: "/matches", label: "Matches", Icon: IconMatches },
  { to: "/me", label: "Me", Icon: IconMe },
];

/**
 * The frame every signed-in screen sits in.
 *
 * Tabs are Board · Events · Log · Matches · Me. Log sits in the middle as a solid ink
 * block, because logging a match is the one thing the whole rating loop depends on and it
 * should be the easiest thing on the screen to hit. Everyone gets the Events tab;
 * `Tournaments.tsx` gates the organiser-only controls per action rather than per tab.
 *
 * The active tab is marked with a heavy ink bar and full-strength ink, never a colour:
 * chrome is never coloured (docs/design-direction.md §2).
 */
export function AppShell({ awaitingCount = 0 }: { awaitingCount?: number }) {
  const { isDemo } = useAuth();

  return (
    <div
      className="min-h-dvh"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        // Clears the fixed nav, which grows by the home-bar inset on notched phones.
        paddingBottom: "calc(6rem + env(safe-area-inset-bottom))",
      }}
    >
      {isDemo ? (
        <p
          className="micro px-4 py-2"
          style={{ color: "var(--color-ink)", background: "var(--color-attention-fill)" }}
        >
          Sample mode · Supabase not configured · nothing is saved
        </p>
      ) : null}

      <Outlet />

      <nav
        className="fixed inset-x-0 bottom-0 z-10 border-t-2 bg-ground"
        style={{ borderColor: "var(--color-line)", paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Primary"
      >
        <ul className="mx-auto flex max-w-2xl">
          {TABS.map((tab) => (
            <li key={tab.to} className="flex-1">
              <NavLink
                to={tab.to}
                className="relative flex flex-col items-center justify-center gap-0.5"
                style={({ isActive }) => ({
                  minHeight: 72,
                  color:
                    isActive || tab.to === "/log" ? "var(--color-ink)" : "var(--color-ink-muted)",
                  fontWeight: isActive || tab.to === "/log" ? 700 : 600,
                  fontSize: 10,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                })}
              >
                {({ isActive }) => (
                  <>
                    {isActive && tab.to !== "/log" ? (
                      <span
                        aria-hidden
                        className="absolute inset-x-[18px] -top-[2px] h-1"
                        style={{ background: "var(--color-line)" }}
                      />
                    ) : null}

                    {/* The count rides the icon rather than the word, so the label stays
                        the same width whether or not anything is waiting. */}
                    <span
                      // Every icon gets the Log block's 46px box, so all five labels share a
                      // baseline whether or not their icon is a solid block.
                      className={
                        tab.to === "/log"
                          ? "grid size-[46px] place-items-center"
                          : "relative grid h-[46px] place-items-center"
                      }
                      style={
                        tab.to === "/log"
                          ? { background: "var(--color-ink)", color: "var(--color-ground)" }
                          : undefined
                      }
                    >
                      <tab.Icon />
                      {tab.to === "/matches" && awaitingCount > 0 ? (
                        <span
                          className="font-mono absolute -right-3 top-[5px] min-w-[18px] px-1 text-center text-[11px] leading-[18px]"
                          style={{
                            background: "var(--color-attention-fill)",
                            color: "var(--color-ink)",
                            fontWeight: 600,
                          }}
                        >
                          {awaitingCount}
                        </span>
                      ) : null}
                    </span>

                    {tab.label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/**
 * Page heading with the eyebrow above it. The title is the scoresheet's header: tall,
 * condensed, upper case, and big enough that the page needs no other decoration.
 */
export function PageHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow?: string;
  title: string;
  /**
   * A secondary control on the eyebrow line, right-aligned — an organiser's "New tournament".
   * Up here it stays out of the content's way; as a black button between the title and the
   * list it was the loudest thing on a screen most people only read.
   */
  action?: ReactNode;
}) {
  return (
    <header className="mb-6">
      {eyebrow || action ? (
        <div className="mb-1.5 flex min-h-[20px] items-center justify-between gap-3">
          {eyebrow ? <div className="micro">{eyebrow}</div> : <span />}
          {action}
        </div>
      ) : null}
      <h1
        className="font-display uppercase"
        style={{ fontSize: "clamp(52px, 17vw, 78px)", fontWeight: 900, lineHeight: 0.84 }}
      >
        {title}
      </h1>
    </header>
  );
}
